// The laws of the table. Pure functions over plain, unchanging data.
//
// A column lists its cards from its base to its free end. A mirror column
// shows every card turned over (negated), and the laws read what is shown.
// A tide column turns itself over after every fold in it. An altar holds one
// card or is empty.

import { replaced, reversed, sorted } from "./arrays.ts"
import { err, ok, type Result } from "./result.ts"
import {
  keyOf,
  negate,
  RAISED,
  ringAt,
  ringIndexes,
  withRing,
  type Ring,
  type RingIndex,
  type Rings,
  type Width,
} from "./rings.ts"

export type CardId = number
export type Card = { readonly id: CardId; readonly rings: Rings }
export type Column = { readonly mirror: boolean; readonly tide: boolean; readonly cards: readonly Card[] }
export type Altar = Card | "empty"
export type TableState = {
  readonly width: Width
  readonly cols: readonly Column[]
  readonly altars: readonly Altar[]
  readonly nextId: CardId
}

// A deal: the cards of each column before play, and how many altars.
export type DealColumn = { readonly mirror: boolean; readonly tide: boolean; readonly cards: readonly Rings[] }
export type Deal = { readonly width: Width; readonly cols: readonly DealColumn[]; readonly altars: number }

// `n` is how many cards are lifted from the free end of a column.
export type From = { readonly kind: "col"; readonly i: number; readonly n: number } | { readonly kind: "altar"; readonly i: number }
export type To = { readonly kind: "col"; readonly i: number } | { readonly kind: "altar"; readonly i: number }
export type Move = { readonly from: From; readonly to: To }

// Why a move was refused: the law it would break.
export type Refusal =
  | { readonly why: "none" } // nothing there to lift, or nowhere to put it
  | { readonly why: "same" } // the source is the destination
  | { readonly why: "run" } // the lifted cards are not a run
  | { readonly why: "altar" } // the altar is taken, or more than one card
  | { readonly why: "burden"; readonly cap: number; readonly n: number }
  | { readonly why: "rest"; readonly card: Card; readonly onto: Card; readonly faults: readonly RingIndex[] }

// What happened, in order. `after` is the table right after the step, so the
// table can animate one step at a time.
export type MoveEvent = {
  readonly type: "move"
  readonly ids: readonly CardId[] // in the order they were laid down
  readonly from: From
  readonly to: To
  readonly flip: boolean // the cards turned over on the way
  readonly after: TableState
}
export type FoldEvent = {
  readonly type: "fold"
  readonly col: number
  readonly at: number
  readonly ring: RingIndex
  readonly folded: readonly [Card, Card, Card]
  readonly mirror: boolean // the column's kind when the fold happened
  readonly card: Card
  readonly after: TableState
}
export type TurnEvent = { readonly type: "turn"; readonly col: number; readonly mirror: boolean; readonly after: TableState }
export type WinEvent = { readonly type: "win" }
export type GameEvent = MoveEvent | FoldEvent | TurnEvent | WinEvent
export type Outcome = { readonly state: TableState; readonly events: readonly GameEvent[] }

// The cards a move lifts, deepest first: never none.
export type Lift = { readonly cards: readonly [Card, ...Card[]]; readonly mirror: boolean }

// ---------- cards and columns ----------

export const seen = (card: Card, mirror: boolean): Rings => (mirror ? negate(card.rings) : card.rings)

export const heldCards = (altars: readonly Altar[]): readonly Card[] => altars.flatMap((a) => (a === "empty" ? [] : [a]))

const isEmptyAltar = (a: Altar): boolean => a === "empty"
const isEmptyColumn = (c: Column): boolean => c.cards.length === 0

export const cardCount = (state: TableState): number =>
  state.cols.reduce((n, c) => n + c.cards.length, 0) + heldCards(state.altars).length

export const isWon = (state: TableState): boolean => cardCount(state) === 1

// ---------- the law of rest ----------

const foldedAt = (rings: Rings, i: RingIndex): boolean => ringAt(rings, i) === "folded"
const sameFolds = (x: Rings, y: Rings): boolean => ringIndexes(x).every((i) => foldedAt(x, i) === foldedAt(y, i))
const differsAt =
  (x: Rings, y: Rings) =>
  (i: RingIndex): boolean =>
    ringAt(x, i) !== ringAt(y, i)
const oneNotchBelow = (a: Ring | undefined, b: Ring | undefined): boolean =>
  typeof a === "number" && typeof b === "number" && RAISED[a] === b

// x may rest on y when y is x with exactly one open ring raised one notch
// (down to level, or level to up), and both have the same rings folded.
// Gives the raised ring.
export const restRing = (x: Rings, y: Rings): RingIndex | undefined => {
  if (x.length !== y.length || !sameFolds(x, y)) return undefined
  const [ring, extra] = ringIndexes(x).filter(differsAt(x, y))
  if (ring === undefined || extra !== undefined) return undefined
  return oneNotchBelow(ringAt(x, ring), ringAt(y, ring)) ? ring : undefined
}

export const rests = (x: Rings, y: Rings): boolean => restRing(x, y) !== undefined

// Which rings stop x resting on y. Shown to the player as rust.
export const restFaults = (x: Rings, y: Rings): readonly RingIndex[] => {
  const faults = ringIndexes(x).filter(differsAt(x, y))
  const [only, extra] = faults
  if (only !== undefined && extra === undefined && oneNotchBelow(ringAt(x, only), ringAt(y, only))) return []
  return faults
}

const restsIn = (mirror: boolean, upper: Card | undefined, lower: Card | undefined): boolean =>
  upper !== undefined && lower !== undefined && rests(seen(upper, mirror), seen(lower, mirror))

// How many cards at the free end of a column form a run.
export const runLength = (col: Column): number => {
  const cards = col.cards
  if (cards.length === 0) return 0
  let n = 1
  for (let i = cards.length - 1; i > 0 && restsIn(col.mirror, cards[i], cards[i - 1]); i--) n++
  return n
}

// ---------- the law of burden ----------

// How many cards may move as one between two columns of the same kind: what
// could be moved one card at a time through free altars and empty columns.
// An empty column of the other kind lifts any run, since pouring through it
// turns the stack over card by card.
export const capacity = (state: TableState, fromCol: number, toCol: number): number => {
  const kind = state.cols[fromCol]?.mirror ?? false
  const empties = state.cols.filter((c, k) => k !== fromCol && k !== toCol && isEmptyColumn(c))
  if (empties.some((c) => c.mirror !== kind)) return Infinity
  const free = state.altars.filter(isEmptyAltar).length
  return (free + 1) * 2 ** empties.length
}

// ---------- judging a move ----------

const lastOf = (cards: readonly [Card, ...Card[]]): Card => cards.reduce((_, c) => c)

// The top `n` cards of a column, if it has that many and `n` is at least one.
const liftFrom = (col: Column, n: number): Lift | undefined => {
  if (n > col.cards.length) return undefined
  const [deepest, ...rest] = col.cards.slice(col.cards.length - n)
  return deepest === undefined ? undefined : { cards: [deepest, ...rest], mirror: col.mirror }
}

// The cards a move would lift, and the kind of column they come from.
export const lifted = (state: TableState, from: From): Lift | undefined => {
  switch (from.kind) {
    case "altar": {
      const a = state.altars[from.i]
      return a === undefined || a === "empty" ? undefined : { cards: [a], mirror: false }
    }
    case "col": {
      const col = state.cols[from.i]
      return col === undefined ? undefined : liftFrom(col, from.n)
    }
  }
}

const sameAltar = (from: From, to: To): boolean => from.kind === "altar" && to.kind === "altar" && from.i === to.i
const sameColumn = (from: From, to: To): boolean => from.kind === "col" && to.kind === "col" && from.i === to.i

const checkAltar = (state: TableState, from: From, i: number, lift: Lift): Result<Refusal, Lift> => {
  if (sameAltar(from, { kind: "altar", i })) return err({ why: "same" })
  const altar = state.altars[i]
  if (altar === undefined) return err({ why: "none" })
  if (lift.cards.length > 1 || altar !== "empty") return err({ why: "altar" })
  return ok(lift)
}

const checkColumn = (state: TableState, from: From, i: number, lift: Lift): Result<Refusal, Lift> => {
  if (sameColumn(from, { kind: "col", i })) return err({ why: "same" })
  const dest = state.cols[i]
  if (dest === undefined) return err({ why: "none" })
  const flip = lift.mirror !== dest.mirror
  const n = lift.cards.length
  const cap = flip || n === 1 ? Infinity : capacity(state, from.i, i)
  if (n > cap) return err({ why: "burden", cap, n })
  const top = dest.cards.at(-1)
  if (top === undefined) return ok(lift)
  // Poured over, the run turns: its top card lands first.
  const touching = flip ? lastOf(lift.cards) : lift.cards[0]
  const x = seen(touching, dest.mirror)
  const y = seen(top, dest.mirror)
  return rests(x, y) ? ok(lift) : err({ why: "rest", card: touching, onto: top, faults: restFaults(x, y) })
}

// Is this move allowed? A refusal names the broken law.
export const checkMove = (state: TableState, m: Move): Result<Refusal, Lift> => {
  const lift = lifted(state, m.from)
  if (lift === undefined) return err({ why: "none" })
  const source = m.from.kind === "col" ? state.cols[m.from.i] : undefined
  if (source !== undefined && lift.cards.length > runLength(source)) return err({ why: "run" })
  switch (m.to.kind) {
    case "altar":
      return checkAltar(state, m.from, m.to.i, lift)
    case "col":
      return checkColumn(state, m.from, m.to.i, lift)
  }
}

// ---------- the law of fold ----------

export type FoldSpot = { readonly at: number; readonly ring: RingIndex; readonly cards: readonly [Card, Card, Card] }

// Three cards in a run that differ in one ring only, reading up, level, down
// from the base outward. Gives the first card's place, the ring and the cards.
export const findFold = (col: Column): FoldSpot | undefined => {
  const show = (c: Card): Rings => seen(c, col.mirror)
  for (let at = 0; at + 2 < col.cards.length; at++) {
    const [a, b, d] = col.cards.slice(at, at + 3)
    if (a === undefined || b === undefined || d === undefined) return undefined
    const ring = restRing(show(b), show(a))
    if (ring !== undefined && restRing(show(d), show(b)) === ring) return { at, ring, cards: [a, b, d] }
  }
  return undefined
}

type Settled = { readonly state: TableState; readonly events: readonly GameEvent[] }

const replaceColumn = (state: TableState, i: number, col: Column): TableState => ({ ...state, cols: replaced(state.cols, i, col) })

// One fold at a known spot. A tide column then turns over: the stack
// reverses and every card is seen negated, so every run stays a run and the
// card buried deepest comes to the free end.
const foldAt = (state: TableState, ci: number, col: Column, spot: FoldSpot): Settled => {
  const card: Card = { id: state.nextId, rings: withRing(spot.cards[1].rings, spot.ring, "folded") }
  const cards = [...col.cards.slice(0, spot.at), card, ...col.cards.slice(spot.at + 3)]
  const folded = { ...replaceColumn(state, ci, { ...col, cards }), nextId: state.nextId + 1 }
  const fold: FoldEvent = { type: "fold", col: ci, at: spot.at, ring: spot.ring, folded: spot.cards, mirror: col.mirror, card, after: folded }
  if (!col.tide) return { state: folded, events: [fold] }
  const turned = replaceColumn(folded, ci, { ...col, mirror: !col.mirror, cards: reversed(cards) })
  return { state: turned, events: [fold, { type: "turn", col: ci, mirror: !col.mirror, after: turned }] }
}

const firstFold = (state: TableState): { readonly ci: number; readonly col: Column; readonly spot: FoldSpot } | undefined => {
  for (const [ci, col] of state.cols.entries()) {
    const spot = findFold(col)
    if (spot !== undefined) return { ci, col, spot }
  }
  return undefined
}

// Fold everything that can fold, until nothing can. Folds cascade.
export const settle = (start: TableState): Settled => {
  let state = start
  const events: GameEvent[] = []
  for (let next = firstFold(state); next !== undefined; next = firstFold(state)) {
    const step = foldAt(state, next.ci, next.col, next.spot)
    state = step.state
    events.push(...step.events)
  }
  return { state, events }
}

// ---------- playing a move ----------

const takeFrom = (state: TableState, from: From): TableState => {
  switch (from.kind) {
    case "altar":
      return { ...state, altars: replaced(state.altars, from.i, "empty") }
    case "col": {
      const col = state.cols[from.i]
      return col === undefined ? state : replaceColumn(state, from.i, { ...col, cards: col.cards.slice(0, col.cards.length - from.n) })
    }
  }
}

type Laid = { readonly state: TableState; readonly ids: readonly CardId[]; readonly flip: boolean }

const putTo = (state: TableState, to: To, lift: Lift): Laid => {
  switch (to.kind) {
    case "altar": {
      const card = lift.cards[0]
      return { state: { ...state, altars: replaced(state.altars, to.i, card) }, ids: [card.id], flip: lift.mirror }
    }
    case "col": {
      const dest = state.cols[to.i]
      if (dest === undefined) return { state, ids: [], flip: false }
      const flip = lift.mirror !== dest.mirror
      const laid = flip ? reversed(lift.cards) : lift.cards
      return { state: replaceColumn(state, to.i, { ...dest, cards: [...dest.cards, ...laid] }), ids: laid.map((c) => c.id), flip }
    }
  }
}

// Play a move: the new table and everything that happened, in order.
export const applyMove = (state: TableState, m: Move): Result<Refusal, Outcome> => {
  const verdict = checkMove(state, m)
  if (verdict.tag === "error") return verdict
  const laid = putTo(takeFrom(state, m.from), m.to, verdict.value)
  const moved: MoveEvent = { type: "move", ids: laid.ids, from: m.from, to: m.to, flip: laid.flip, after: laid.state }
  const settled = settle(laid.state)
  const win: readonly GameEvent[] = isWon(settled.state) ? [{ type: "win" }] : []
  return ok({ state: settled.state, events: [moved, ...settled.events, ...win] })
}

// ---------- the start of a rite ----------

export const createState = (deal: Deal): TableState => {
  let id = 1
  const make = (rings: Rings): Card => ({ id: id++, rings })
  const cols = deal.cols.map((c) => ({ mirror: c.mirror, tide: c.tide, cards: c.cards.map(make) }))
  const altars: readonly Altar[] = Array.from({ length: deal.altars }, () => "empty")
  return settle({ width: deal.width, cols, altars, nextId: id }).state
}

// ---------- every move, for the solver and the glimmer ----------

const columnKind = (c: Column): number => (c.mirror ? 1 : 0) + (c.tide ? 2 : 0)

const sameKind = (a: Column, b: Column): boolean => a.mirror === b.mirror && a.tide === b.tide

// A column a card may go to. With `prune`, an empty column only if it is the
// first empty one of its kind: the others would make the same table.
const isDestination = (state: TableState, prune: boolean) => (col: Column, j: number): boolean =>
  !prune || !isEmptyColumn(col) || state.cols.findIndex((c) => isEmptyColumn(c) && columnKind(c) === columnKind(col)) === j

const destinations = (state: TableState, prune: boolean): readonly number[] =>
  state.cols.flatMap((col, j) => (isDestination(state, prune)(col, j) ? [j] : []))

export type MoveOptions = { readonly prune: boolean }

// What every candidate move on one table shares.
type Search = { readonly state: TableState; readonly prune: boolean; readonly dests: readonly number[] }

// With `prune`, a whole column poured into an empty column of its own kind
// leaves the same table.
const isSameTable = (s: Search, col: Column, n: number, j: number): boolean => {
  const dest = s.state.cols[j]
  return s.prune && n === col.cards.length && dest !== undefined && isEmptyColumn(dest) && sameKind(dest, col)
}

const runMoves = (s: Search, col: Column, i: number) => (n: number): readonly Move[] =>
  s.dests.filter((j) => j !== i && !isSameTable(s, col, n, j)).map((j): Move => ({ from: { kind: "col", i, n }, to: { kind: "col", i: j } }))

// The empty altars a card may go to; with `prune`, only the first.
const freeAltars = (s: Search): readonly number[] => {
  const free = [...s.state.altars.keys()].filter((k) => s.state.altars[k] === "empty")
  return s.prune ? free.slice(0, 1) : free
}

const altarMoves = (s: Search, i: number): readonly Move[] => freeAltars(s).map((k): Move => ({ from: { kind: "col", i, n: 1 }, to: { kind: "altar", i: k } }))

// Every lift from one column: each length of its run to each column, then
// its top card to the altars.
const columnMoves = (s: Search) => (col: Column, i: number): readonly Move[] => {
  const run = runLength(col)
  const lengths = Array.from({ length: run }, (_, k) => k + 1)
  return [...lengths.flatMap(runMoves(s, col, i)), ...(run === 0 ? [] : altarMoves(s, i))]
}

const heldMoves = (s: Search) => (a: Altar, i: number): readonly Move[] =>
  a === "empty" ? [] : s.dests.map((j): Move => ({ from: { kind: "altar", i }, to: { kind: "col", i: j } }))

const isLegal = (state: TableState) => (m: Move): boolean => checkMove(state, m).tag === "ok"

// Every legal move. With `prune`, moves that lead to the same table are
// dropped for search: only the first empty altar, only one empty column of
// each kind, and never a whole column into an empty column of its own kind.
export const legalMoves = (state: TableState, { prune }: MoveOptions): readonly Move[] => {
  const s: Search = { state, prune, dests: destinations(state, prune) }
  return [...state.cols.flatMap(columnMoves(s)), ...state.altars.flatMap(heldMoves(s))].filter(isLegal(state))
}

// ---------- keys and measures for search ----------

const cardKey = (c: Card): string => keyOf(c.rings)
const columnKey = (c: Column): string => `${c.tide ? "t" : ""}${c.mirror ? "m" : "n"}${c.cards.map(cardKey).join(",")}`

// The same for tables that differ only in column order (within each kind)
// or altar order. The solver uses it to skip repeats.
export const stateKey = (state: TableState): string =>
  `${sorted(state.cols.map(columnKey)).join("|")}#${sorted(heldCards(state.altars).map(cardKey)).join(",")}`

const altarKey = (a: Altar): string => (a === "empty" ? "_" : cardKey(a))

// The table exactly as laid out, column by column and altar by altar. Moves
// name columns and altars, so anything saved with moves is keyed by this.
export const layoutKey = (state: TableState): string => `${state.cols.map(columnKey).join("|")}#${state.altars.map(altarKey).join(",")}`

const columnBreaks = (col: Column): number => col.cards.filter((c, k) => k > 0 && !restsIn(col.mirror, c, col.cards[k - 1])).length

// How many cards sit on a card they do not rest on. Each must move at least once.
export const breaks = (state: TableState): number => state.cols.reduce((n, col) => n + columnBreaks(col), 0)
